import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryEntity } from '@actual-app/core/types/models';

import { abrirEntradaAtras } from '#components/mobile/ui/atras';
import { useFormat } from '#hooks/useFormat';
import { useSheetValue } from '#hooks/useSheetValue';
import { useUndo } from '#hooks/useUndo';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  EMPTY_EXPRESSION,
  evaluate,
  isDirty,
  pressBackspace,
  pressDigit,
  pressEquals,
  pressOperator,
  ZERO_EXPRESSION,
} from './assignExpression';
import type { AssignExpression, Operator } from './assignExpression';
import type { CambioBorrador } from './borrador';

export type KeypadKey =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | Operator
  | '='
  | 'backspace'
  /** ✕ «Poner a 0». */
  | 'cero';

/**
 * Modo borrador (6-oct, «Asignar el mes»): lo que confirma el teclado no va
 * al presupuesto sino a un borrador que guarda la pantalla. En el Plan no se
 * pasa y el teclado guarda al momento, como siempre.
 */
export type ModoBorradorTeclado = {
  /** Cambio de la categoría en el borrador, si lo hay. */
  cambioDe: (id: CategoryEntity['id']) => CambioBorrador | undefined;
  /** Pone el asignado (absoluto) de la categoría en el borrador. */
  asignar: (category: CategoryEntity, amount: IntegerAmount) => void;
  /** Marca o desmarca «Suficiente» este mes en el borrador. */
  marcarSuficiente: (category: CategoryEntity, suficiente: boolean) => void;
};

export type AssignKeypadContextValue = {
  month: string;
  /** Categoría seleccionada (con el panel abierto) o `null`. */
  selectedCategory: CategoryEntity | null;
  expression: AssignExpression;
  /** Importe asignado vivo (hoja) de la categoría seleccionada. */
  liveBudgeted: IntegerAmount;
  /** Abre el panel para esta categoría; aplica lo pendiente de la anterior. */
  select: (category: CategoryEntity) => void;
  press: (key: KeypadKey) => void;
  /** Aplica la expresión (budget-amount) y cierra. */
  done: () => void;
  /** Descarta lo tecleado y cierra. */
  cancel: () => void;
  /** Vuelve a la expresión vacía sin cerrar (tras un auto-asignar). */
  reset: () => void;
  onBudgetAction: (month: string, action: string, args: unknown) => void;
  /** Alto del panel en px, para dejar sitio al final de la lista. */
  panelHeight: number;
  setPanelHeight: (height: number) => void;
  /** Modo borrador; `null` = guarda al momento (Plan). */
  borrador: ModoBorradorTeclado | null;
};

const AssignKeypadContext = createContext<AssignKeypadContextValue | null>(
  null,
);

/** `null` fuera del proveedor (escritorio, tests): la celda usa el modal. */
export function useAssignKeypad() {
  return useContext(AssignKeypadContext);
}

type AssignKeypadProviderProps = {
  month: string;
  onBudgetAction: (month: string, action: string, args: unknown) => void;
  /** Con él, «Hecho» y el botón verde escriben en el borrador. */
  borrador?: ModoBorradorTeclado | null;
  children: ReactNode;
};

export function AssignKeypadProvider({
  month,
  onBudgetAction,
  borrador = null,
  children,
}: AssignKeypadProviderProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const { showUndoNotification } = useUndo();

  const [selectedCategory, setSelectedCategory] =
    useState<CategoryEntity | null>(null);
  const [expression, setExpression] =
    useState<AssignExpression>(EMPTY_EXPRESSION);
  const [panelHeight, setPanelHeight] = useState(0);

  const selectedId = selectedCategory?.id ?? '';
  const asignadoHoja =
    useSheetValue<'envelope-budget', 'budget'>(
      envelopeBudget.catBudgeted(selectedId),
    ) ?? 0;
  // En modo borrador, la base de la expresión es lo que dice el borrador.
  const liveBudgeted =
    (selectedCategory && borrador?.cambioDe(selectedId)?.asignado) ??
    asignadoHoja;

  // Referencias para que `select` pueda aplicar lo pendiente de la categoría
  // anterior sin cambiar de identidad en cada tecla.
  const stateRef = useRef({ selectedCategory, expression, liveBudgeted });
  stateRef.current = { selectedCategory, expression, liveBudgeted };

  const apply = useCallback(
    (category: CategoryEntity, expr: AssignExpression, live: IntegerAmount) => {
      if (!isDirty(expr)) {
        return;
      }
      const amount = evaluate(expr, live);
      if (amount === live) {
        return;
      }
      if (borrador) {
        borrador.asignar(category, amount);
        return;
      }
      onBudgetAction(month, 'budget-amount', {
        category: category.id,
        amount,
      });
      showUndoNotification({
        message: t('{{categoryName}} budget has been updated to {{amount}}.', {
          categoryName: category.name,
          amount: format(amount, 'financial'),
        }),
      });
    },
    [borrador, format, month, onBudgetAction, showUndoNotification, t],
  );

  const select = useCallback(
    (category: CategoryEntity) => {
      const current = stateRef.current;
      if (
        current.selectedCategory &&
        current.selectedCategory.id !== category.id
      ) {
        apply(
          current.selectedCategory,
          current.expression,
          current.liveBudgeted,
        );
      }
      if (current.selectedCategory?.id !== category.id) {
        setExpression(EMPTY_EXPRESSION);
      }
      setSelectedCategory(category);
    },
    [apply],
  );

  const press = useCallback((key: KeypadKey) => {
    const live = stateRef.current.liveBudgeted;
    setExpression(expr => {
      switch (key) {
        case '+':
        case '-':
          return pressOperator(expr, key, live);
        case '=':
          return pressEquals(expr, live);
        case 'backspace':
          return pressBackspace(expr);
        case 'cero':
          return ZERO_EXPRESSION;
        default:
          return pressDigit(expr, key);
      }
    });
  }, []);

  const done = useCallback(() => {
    const current = stateRef.current;
    if (current.selectedCategory) {
      apply(current.selectedCategory, current.expression, current.liveBudgeted);
    }
    setSelectedCategory(null);
    setExpression(EMPTY_EXPRESSION);
  }, [apply]);

  const cancel = useCallback(() => {
    setSelectedCategory(null);
    setExpression(EMPTY_EXPRESSION);
  }, []);

  const reset = useCallback(() => {
    setExpression(EMPTY_EXPRESSION);
  }, []);

  // «Atrás» del móvil cierra el teclado (guardando lo escrito) en vez de salir
  // de la pantalla: al abrirlo se añade al historial una entrada con la misma
  // URL; si se cierra con «Hecho» o «✕», se quita esa entrada (ver ui/atras).
  const abierto = selectedCategory != null;
  const doneRef = useRef(done);
  useEffect(() => {
    doneRef.current = done;
  }, [done]);
  useEffect(() => {
    if (!abierto) {
      return;
    }
    const entrada = abrirEntradaAtras(() => doneRef.current());
    return () => entrada.quitar();
  }, [abierto]);

  // Al cambiar de mes se cierra el panel: la expresión era de otro mes.
  useEffect(() => {
    setSelectedCategory(null);
    setExpression(EMPTY_EXPRESSION);
  }, [month]);

  const value = useMemo<AssignKeypadContextValue>(
    () => ({
      month,
      selectedCategory,
      expression,
      liveBudgeted,
      select,
      press,
      done,
      cancel,
      reset,
      onBudgetAction,
      panelHeight,
      setPanelHeight,
      borrador,
    }),
    [
      month,
      selectedCategory,
      expression,
      liveBudgeted,
      select,
      press,
      done,
      cancel,
      reset,
      onBudgetAction,
      panelHeight,
      borrador,
    ],
  );

  return (
    <AssignKeypadContext.Provider value={value}>
      {children}
    </AssignKeypadContext.Provider>
  );
}
